import { isEmpty, isNil } from 'lodash';
import { useCallback, useEffect, useId, useRef } from 'react';
import { RRule } from 'rrule';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { useBatch } from '@shared/scenes/cards/entityBulkActions/BatchCreateContext';
import { isDateComponent } from '@shared/scenes/workflows/utils';
import { getAnchorName } from '@shared/styles';
import { useBoolean } from '@shared/util/hooks';
import { EditFieldContainer } from '../EditFieldContainer';
import { DateEditFieldPicker } from './DateEditFieldPicker';
import { RepeatText } from './RepeatText';
import { useBatchRule } from './hooks/useBatchRule';
import { useEntityBatch } from './hooks/useEntityBatch';
import { useIsNeverEnabled } from './hooks/useIsNeverEnabled';
import { DateProps, RepeatEndType } from './types';
import { buildCustomRRule, getRruleText } from './utils';

export function DateEditField({
  field,
  component,
  updateField,
  workflowTemplateId,
  entityBatchField,
  error,
  includeWrapperPadding = true,
  allowClear = true,
  showIcon,
  label,
  showLabel = true,
  header,
  footer,
  subfooter,
  isGrayScale,
  isItalic,
  style,
}: DateProps) {
  const { data: entityBatch, loading: entityBatchLoading } = useEntityBatch(
    entityBatchField?.id,
    workflowTemplateId,
  );
  const fieldId = useId();
  const anchorName = getAnchorName('date-edit', component.id, fieldId);
  const onClear = useCallback(() => {
    updateField?.(component.id, null);
  }, [component.id, updateField]);
  const {
    rruleOptions,
    setRruleOptions,
    canBatchCreate,
    userIsEditing,
    setUserIsEditing,
    minimumInSeries,
    tagFilter,
    setMinimumInSeries,
    setTagFilter,
    recurrenceType,
    offset,
    setOffset,
  } = useBatch();
  const isReadOnly = component.readonly || isNil(updateField);

  // Validate offset configuration
  const hasValidTagFilter =
    !isNil(tagFilter?.componentId) && !isEmpty(tagFilter?.value?.containsString?.values);
  const hasValidOffset = !isNil(offset?.unit) && !isNil(offset?.value) && offset.value > 0;
  const isValidOneAtATime = hasValidTagFilter && hasValidOffset;

  const showRepeatText =
    isDateComponent(component) &&
    component.allowEntityBatchCreation &&
    (!isNil(rruleOptions) || isValidOneAtATime);
  const [isDatePickerOpen, showDatePicker, hideDatePicker] = useBoolean(false);
  const isBatchRuleEnabled =
    isDateComponent(component) && canBatchCreate && !!component.allowEntityBatchCreation;

  // If we're loading an existing entity batch, wait for it to load before initializing batch rule
  const isWaitingForEntityBatch = !isNil(entityBatchField?.id) && entityBatchLoading;

  const {
    repeatPresets,
    filteredRepeatPresets,
    repeatCount,
    setRepeatCount,
    presetIndexSelected,
    setPresetIndexSelected,
    isCustomRepeatSelected,
    isOffsetSelected,
    repeatUntil,
    setRepeatUntil,
    repeatEndType,
    setRepeatEndType,
    resetBatchOptions,
  } = useBatchRule({
    rruleOptions,
    setRruleOptions,
    recurrenceType,
    field,
    isBatchRuleEnabled,
    userIsEditing,
    component: isDateComponent(component) ? component : undefined,
    setMinimumInSeries,
    setTagFilter,
    isWaitingForEntityBatch,
  });

  // Determine if the "Never" end-type option is currently valid
  const isNeverEndTypeEnabled = useIsNeverEnabled({ minimumInSeries, tagFilter });

  // revert to end date when date picker is closed, if never is not valid
  const prevDatePickerOpenRef = useRef<boolean>(false);

  useEffect(() => {
    // Skip if batch rule is not enabled
    if (!isBatchRuleEnabled) {
      prevDatePickerOpenRef.current = isDatePickerOpen;
      return;
    }
    const closed = prevDatePickerOpenRef.current && !isDatePickerOpen;

    if (closed && repeatEndType === RepeatEndType.NEVER && !isNeverEndTypeEnabled) {
      setRepeatEndType(RepeatEndType.END_DATE);
    }

    prevDatePickerOpenRef.current = isDatePickerOpen;
  }, [
    isDatePickerOpen,
    repeatEndType,
    isNeverEndTypeEnabled,
    setRepeatEndType,
    isBatchRuleEnabled,
  ]);

  // set rrule options when entity batch is set
  // Use a ref to track the last rrule string we've seen to avoid re-parsing on every render
  const lastRruleStringRef = useRef<string | null>(null);
  useEffect(() => {
    // Only sync rrule options when entity batch creation is enabled for this component
    if (!isDateComponent(component) || !component.allowEntityBatchCreation) {
      return;
    }
    if (isNil(entityBatch)) {
      return;
    }
    const rrule = entityBatch.getRecurringEntitiesSchedule.rrule;
    if (isNil(rrule)) {
      return;
    }
    // Skip if we've already processed this exact rrule string
    if (lastRruleStringRef.current === rrule) {
      return;
    }
    lastRruleStringRef.current = rrule;
    setRruleOptions(RRule.fromString(rrule).options);
  }, [entityBatch, setRruleOptions, component]);

  // keep the bymonthday in sync with the start date when yearly
  useEffect(() => {
    // Skip if batch rule is not enabled - these sync operations are only needed for batch creation
    if (!isBatchRuleEnabled) {
      return;
    }
    if (isNil(field?.date)) {
      return;
    }

    const startDateDay = new Date(field.date).getDate();
    const startMonth = new Date(field.date).getMonth() + 1;

    if (recurrenceType === 'RRULE_SCHEDULE') {
      setRruleOptions(prev => {
        if (prev?.freq !== RRule.YEARLY) {
          return prev;
        } else {
          const prevByMonthDay = prev?.bymonthday;
          const prevByMonth = prev?.bymonth;
          const hasSameDay = Array.isArray(prevByMonthDay)
            ? prevByMonthDay.length === 1 && prevByMonthDay[0] === startDateDay
            : prevByMonthDay === startDateDay;
          const hasSameMonth = Array.isArray(prevByMonth)
            ? prevByMonth.length === 1 && prevByMonth[0] === startMonth
            : prevByMonth === startMonth;
          if (hasSameDay && hasSameMonth) {
            return prev;
          }

          return buildCustomRRule(
            prev,
            { bymonthday: [startDateDay], bymonth: [startMonth] },
            true,
          );
        }
      });
    } else if (recurrenceType === 'OFFSET') {
      setOffset(prev => {
        const newUnit = prev?.unit ?? 'DAY';
        const newValue = prev?.value ?? 1;
        // Only return a new object if values actually changed
        if (
          prev?.unit === newUnit &&
          prev?.value === newValue &&
          prev?.initialDate === field.date
        ) {
          return prev;
        }
        return {
          unit: newUnit,
          value: newValue,
          initialDate: field.date,
        };
      });
    }
  }, [field?.date, setRruleOptions, setOffset, recurrenceType, isBatchRuleEnabled]);

  const handleClear = () => {
    onClear();
    if (isBatchRuleEnabled) {
      resetBatchOptions();
    }
  };

  const hasValue = !!field?.formattedDate;
  const showClearButton = hasValue && !isReadOnly && allowClear;

  const repeatText =
    showRepeatText && rruleOptions ? `Repeats ${getRruleText(rruleOptions)}` : undefined;
  const printContent = [field?.formattedDate, repeatText].filter(Boolean).join('\n') || undefined;

  const showFooter = !isNil(footer) || showRepeatText;

  return (
    <EditFieldContainer
      component={component}
      anchorName={anchorName}
      onPress={!isReadOnly ? showDatePicker : undefined}
      showClearButton={showClearButton}
      onClear={handleClear}
      showIcon={showIcon}
      label={label}
      showLabel={showLabel}
      error={error}
      includeWrapperPadding={includeWrapperPadding}
      subfooter={subfooter}
      isGrayScale={isGrayScale}
      isItalic={isItalic}
      isReadOnly={isReadOnly}
      header={header}
      footer={
        showFooter && (
          <>
            {footer}
            {showRepeatText && (
              <View paddingVertical={0.5}>
                <RepeatText
                  rruleOptions={rruleOptions}
                  includeStartDate
                  offset={offset}
                  tagFilter={tagFilter}
                  workflowTemplateId={workflowTemplateId}
                />
              </View>
            )}
          </>
        )
      }
      style={style}
      printContent={printContent}
    >
      {hasValue && (
        <View paddingRight={1}>
          <Text size="body1">{field.formattedDate}</Text>
        </View>
      )}
      {!hasValue && (
        <View paddingVertical={1.25}>
          <Text size="body1" inactive>
            {component.placeholder}
          </Text>
        </View>
      )}
      {isDateComponent(component) && isDatePickerOpen && (
        <DateEditFieldPicker
          field={field}
          component={component}
          updateField={updateField}
          onClose={hideDatePicker}
          anchorName={anchorName}
          repeatPresets={repeatPresets}
          filteredRepeatPresets={filteredRepeatPresets}
          presetIndexSelected={presetIndexSelected}
          setPresetIndexSelected={setPresetIndexSelected}
          repeatCount={repeatCount}
          setRepeatCount={setRepeatCount}
          repeatUntil={repeatUntil}
          setRepeatUntil={setRepeatUntil}
          repeatEndType={repeatEndType}
          setRepeatEndType={setRepeatEndType}
          isCustomRepeatSelected={isCustomRepeatSelected}
          isOffsetSelected={isOffsetSelected}
          resetBatchOptions={resetBatchOptions}
          setUserIsEditing={setUserIsEditing}
          workflowTemplateId={workflowTemplateId}
        />
      )}
    </EditFieldContainer>
  );
}
